<template>
  <CustomDialog
    v-model="showModal"
    :title="'choose_product'"
    :width="'1000px'"
    :footer="false"
    :close-btn="true"
    @input="handleModalClose"
  >
    <template #activator>
      <div
        class="product-selection-content"
        v-if="product && product.has_variations && product.options"
      >
        <v-container fluid>
          <v-row>
            <!-- Left Column: Product Info & Variations -->
            <v-col cols="12" lg="8">
              <!-- Product Information Card -->
              <v-card outlined class="mb-6 rounded-lg">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  <v-icon small color="primary" left>fal fa-box</v-icon>
                  {{ $t("product_information") }}
                </v-card-title>
                <v-divider class="mt-0" />

                <v-card-text class="pa-4">
                  <v-row>
                    <!-- Product Image -->
                    <v-col cols="12" sm="4" class="text-center">
                      <v-avatar tile size="100" class="product-image-container">
                        <app-img
                          :img="product.img || '/images/no_photo.jpg'"
                          width="100"
                          height="100"
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-icon size="48" color="grey lighten-1"
                                >fal fa-image</v-icon
                              >
                            </v-row>
                          </template>
                        </app-img>
                      </v-avatar>
                    </v-col>

                    <!-- Product Details -->
                    <v-col cols="12" sm="8">
                      <v-row dense>
                        <v-col cols="6" class="text-caption text--secondary">
                          {{ $t("product_name") }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <span class="font-weight-medium">{{
                            product.name
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="text-caption text--secondary">
                          {{ $t("barcode") }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <span class="font-weight-medium">{{
                            product.code
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="text-caption text--secondary">
                          {{ $t("category") }}
                        </v-col>
                        <v-col cols="6" class="text-right">
                          <span class="font-weight-medium">
                            {{
                              product.category?.la_name ||
                              product.category?.en_name ||
                              $t("no_category")
                            }}
                          </span>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Variations Selection with Scrollable Area -->
              <div
                v-if="product && product.has_variations && product.options"
                class="variations-section"
              >
                <v-card outlined class="variations-card rounded-lg">
                  <v-card-title
                    class="text-subtitle-1 font-weight-bold variations-header"
                  >
                    <v-icon small color="primary" left>fal fa-list</v-icon>
                    {{ $t("select_options") }}
                  </v-card-title>

                  <!-- Scrollable Variations Content -->
                  <div class="variations-content">
                    <v-card-text class="py-0">
                      <ProductVariationDisplay
                        :product="{
                          ...product,
                          product_options: product.options,
                          variants: product.variants,
                          track_stock: product.track_stock,
                        }"
                        :selected-options="selectedVariationOptions"
                        :cart-items="defineStore.cart"
                        @option-selected="handleOptionSelected"
                      />
                    </v-card-text>
                  </div>
                </v-card>
              </div>
            </v-col>

            <!-- Right Column: Quantity & Summary -->
            <v-col cols="12" lg="4">
              <div class="sticky-sidebar">
                <!-- Quantity Selection Card -->
                <v-card outlined class="mb-6 rounded-lg">
                  <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon small color="primary" left>fal fa-hashtag</v-icon>
                    {{ $t("quantity") }}
                    <v-spacer />
                    <v-chip
                      small
                      outlined
                      :color="
                        isPackOrDynamic && availableStock === 0
                          ? 'error'
                          : 'info'
                      "
                    >
                      <template v-if="isPackOrDynamic">
                        {{ $t("max_packs") }}:
                        {{
                          product.track_stock === 0 ||
                          product.track_stock === "0"
                            ? $t("unlimited")
                            : availableStock | ToNumber
                        }}
                      </template>
                      <template v-else>
                        {{ $t("available_stock") }}:
                        {{
                          product.track_stock === 0 ||
                          product.track_stock === "0"
                            ? $t("unlimited")
                            : availableStock | ToNumber
                        }}
                      </template>
                    </v-chip>
                  </v-card-title>
                  <v-divider class="mt-0" />

                  <v-card-text class="pa-4">
                    <div class="quantity-controls-container">
                      <!-- Unit Amount Input (for pack/dynamic products) -->
                      <div
                        v-if="isPackOrDynamic"
                        class="unit-amount-section mb-4"
                      >
                        <div class="text-caption text--secondary mb-2">
                          {{ $t("items_per_pack") }}
                        </div>
                        <!-- For dynamic products, make unit amount editable -->
                        <!-- Note: unitAmount has NO max constraint - user can enter any value >= 1 -->
                        <!-- Only quantity (packs) is limited by stock: Math.floor(stock / unitAmount) -->
                        <v-text-field
                          v-if="isDynamic"
                          v-model.number="unitAmount"
                          type="number"
                          :min="1"
                          outlined
                          dense
                          hide-details
                          @input="clampUnitAmount"
                        />
                        <!-- For pack products, show read-only pack amount -->
                        <div v-else class="text-h6">
                          {{ product?.size?.amount || 1 }}
                        </div>
                        <!-- Warning when unitAmount exceeds stock -->
                        <v-alert
                          v-if="
                            isPackOrDynamic &&
                            availableStock === 0 &&
                            product.track_stock !== 0 &&
                            product.track_stock !== '0'
                          "
                          type="warning"
                          dense
                          text
                          class="mt-2"
                        >
                          <small>
                            {{ $t("unit_amount_exceeds_stock") }}.
                            {{ $t("max_packs") }}: 0
                          </small>
                        </v-alert>
                      </div>

                      <!-- Quantity Display -->
                      <div class="quantity-display mb-4">
                        <div class="quantity-number">
                          {{ quantity | ToNumber }}
                        </div>
                        <div class="quantity-label">
                          {{
                            isPackOrDynamic
                              ? $t("number_of_packs")
                              : $t("units")
                          }}
                        </div>
                      </div>

                      <!-- Quantity Controls -->
                      <div class="quantity-buttons mb-4">
                        <v-btn
                          fab
                          small
                          outlined
                          @click="decrementQuantity"
                          :disabled="quantity <= 1"
                          class="quantity-btn"
                          :aria-label="$t('decrease_quantity')"
                        >
                          <v-icon>fal fa-minus</v-icon>
                        </v-btn>

                        <v-slider
                          v-model="quantity"
                          :min="1"
                          :max="
                            product.track_stock === 0 ||
                            product.track_stock === '0'
                              ? 1000
                              : availableStock
                          "
                          :disabled="
                            availableStock === 0 &&
                            product.track_stock !== 0 &&
                            product.track_stock !== '0'
                          "
                          class="quantity-slider mx-4"
                          color="primary"
                          @change="validateQuantity"
                        ></v-slider>

                        <v-btn
                          fab
                          small
                          outlined
                          @click="incrementQuantity"
                          :disabled="
                            quantity >=
                            (product.track_stock === 0 ||
                            product.track_stock === '0'
                              ? 1000
                              : availableStock)
                          "
                          class="quantity-btn"
                          :aria-label="$t('increase_quantity')"
                        >
                          <v-icon>fal fa-plus</v-icon>
                        </v-btn>
                      </div>

                      <!-- Quick Quantity Buttons -->
                      <div class="quick-quantity">
                        <div class="text-caption grey--text mb-2">
                          {{ $t("quick_select") }}:
                        </div>
                        <div class="d-flex flex-wrap gap-2">
                          <v-btn
                            v-for="qty in [1, 5, 10]"
                            :key="qty"
                            small
                            outlined
                            @click="setQuantity(qty)"
                            :disabled="
                              qty >
                              (product.track_stock === 0 ||
                              product.track_stock === '0'
                                ? 1000
                                : availableStock)
                            "
                          >
                            {{ qty | ToNumber }}
                          </v-btn>
                          <v-btn
                            small
                            outlined
                            @click="
                              setQuantity(
                                product.track_stock === 0 ||
                                  product.track_stock === '0'
                                  ? 1000
                                  : availableStock
                              )
                            "
                            :disabled="
                              availableStock === 0 &&
                              product.track_stock !== 0 &&
                              product.track_stock !== '0'
                            "
                          >
                            {{ $t("max") }}
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Price Summary Card -->
                <v-card outlined class="rounded-lg">
                  <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon small color="success" left
                      >fal fa-calculator</v-icon
                    >
                    {{ $t("price_summary") }}
                  </v-card-title>
                  <v-divider class="mt-0" />

                  <v-card-text class="pa-4">
                    <v-row dense>
                      <v-col cols="6" class="text-caption text--secondary">
                        {{ $t("unit_price") }}
                      </v-col>
                      <v-col cols="6" class="text-right">
                        <span class="font-weight-medium">{{
                          unitPrice | NumberFormat
                        }}</span>
                      </v-col>

                      <v-col
                        v-if="isPackOrDynamic"
                        cols="6"
                        class="text-caption text--secondary"
                      >
                        {{ $t("items_per_pack") }}
                      </v-col>
                      <v-col v-if="isPackOrDynamic" cols="6" class="text-right">
                        <span class="font-weight-medium">{{
                          unitAmount | ToNumber
                        }}</span>
                      </v-col>

                      <v-col cols="6" class="text-caption text--secondary">
                        {{ $t("quantity") }}
                      </v-col>
                      <v-col cols="6" class="text-right">
                        <span class="font-weight-medium">{{
                          quantity | ToNumber
                        }}</span>
                      </v-col>

                      <v-divider class="my-3"></v-divider>

                      <v-col cols="6" class="text-subtitle-1 font-weight-bold">
                        {{ $t("total") }}
                      </v-col>
                      <v-col cols="6" class="text-right">
                        <span class="text-h6 font-weight-bold success--text">
                          {{ totalPrice | NumberFormat }}
                        </span>
                      </v-col>
                      <v-col
                        v-if="isPackOrDynamic"
                        cols="12"
                        class="text-caption text--secondary text-center mt-1"
                      >
                        {{ $t("pack_total_formula") }}:
                        {{ unitPrice | NumberFormat }} ×
                        {{ unitAmount | ToNumber }} × {{ quantity | ToNumber }}
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </v-container>

        <!-- Custom Action Buttons -->
        <div class="d-flex justify-end">
          <v-btn
            outlined
            large
            @click="closeModal"
            class="mr-3"
            :disabled="loading"
          >
            <v-icon left>fal fa-times</v-icon>
            {{ $t("cancel") }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            color="success"
            large
            @click="addToCart"
            :disabled="!canAddToCart"
            :loading="loading"
            :aria-label="$t('add_to_cart')"
          >
            <v-icon left>fal fa-cart-plus</v-icon>
            {{ $t("add_to_cart") }}
            <span class="ml-2 font-weight-medium">{{
              totalPrice | NumberFormat
            }}</span>
          </v-btn>
        </div>
      </div>
    </template>
  </CustomDialog>
</template>

<script>
import ProductVariationDisplay from "./ProductVariationDisplay.vue";
import CustomDialog from "@/components/customDialog.vue";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  name: "ProductSelectionModal",
  components: {
    ProductVariationDisplay,
    CustomDialog,
  },
  mixins: [myMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      quantity: 1,
      unitAmount: 1, // For dynamic products, user-entered unit amount
      selectedVariationOptions: {},
      loading: false,
      defineStore: mainStore(),
    };
  },
  computed: {
    showModal: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
    isPackOrDynamic() {
      return (
        this.product?.size?.unit === "pack" ||
        this.product?.size?.unit === "dynamic"
      );
    },
    isDynamic() {
      return this.product?.size?.unit === "dynamic";
    },
    selectedVariant() {
      if (
        !this.product ||
        !this.product.has_variations ||
        !this.product.options ||
        !this.product.variants ||
        !this.product.variants.length
      ) {
        return null;
      }
      // Find the variant that matches all selected options
      const selected = this.selectedVariationOptions;
      return this.product.variants.find((variant) => {
        if (!variant.options) return false;
        return Object.entries(selected).every(
          ([key, value]) => variant.options[key] === value
        );
      });
    },
    availableStock() {
      if (!this.product || !this.product.id) return 0;

      // If track_stock is disabled, allow unlimited stock
      // Check for both number 0 and string "0"
      if (this.product.track_stock === 0 || this.product.track_stock === "0") {
        return 999999; // Large number to represent unlimited stock
      }

      let stock = 0;
      if (this.product.has_variations && this.selectedVariant) {
        stock = this.selectedVariant.stock || 0;
      } else {
        stock = this.product.stock || 0;
      }

      // For pack/dynamic products, calculate max packs/units based on unit amount
      if (this.isPackOrDynamic && this.unitAmount > 0) {
        return Math.floor(stock / this.unitAmount);
      }

      return stock;
    },
    unitPrice() {
      if (!this.product || !this.product.id) return 0;
      if (this.product.has_variations && this.selectedVariant) {
        return this.selectedVariant.price || 0;
      }
      return this.product.price_sale || 0;
    },
    totalPrice() {
      const basePrice = this.unitPrice || 0;
      const qty = this.quantity || 0;

      // For pack/dynamic products, multiply by unit amount
      if (this.isPackOrDynamic && this.unitAmount > 0) {
        return basePrice * this.unitAmount * qty;
      }

      return basePrice * qty;
    },
    canAddToCart() {
      if (!this.product || !this.product.id) return false;

      // Validate unit amount for pack/dynamic products
      // Note: unitAmount can be ANY value >= 1 (no stock-based restriction)
      if (this.isPackOrDynamic && this.unitAmount < 1) {
        return false;
      }

      // If availableStock is 0 (unitAmount > stock), can't add to cart
      if (this.isPackOrDynamic && this.availableStock === 0) {
        return false;
      }

      if (this.product.has_variations) {
        // Require all options to be selected and a valid variant to be found
        const requiredOptions = this.product.options
          ? this.product.options.length
          : 0;
        const selectedCount = Object.keys(this.selectedVariationOptions).length;

        // If track_stock is disabled, only check if options are selected and quantity > 0
        if (
          this.product.track_stock === 0 ||
          this.product.track_stock === "0"
        ) {
          return (
            selectedCount === requiredOptions &&
            this.selectedVariant &&
            this.quantity > 0
          );
        }

        // For pack/dynamic products, validate total units needed
        // Note: We validate quantity × unitAmount <= stock, NOT unitAmount <= stock
        if (this.isPackOrDynamic && this.unitAmount > 0) {
          const totalUnitsNeeded = this.quantity * this.unitAmount;
          let stock = 0;
          if (this.selectedVariant) {
            stock = this.selectedVariant.stock || 0;
          } else {
            stock = this.product.stock || 0;
          }
          return (
            selectedCount === requiredOptions &&
            this.selectedVariant &&
            this.quantity > 0 &&
            totalUnitsNeeded <= stock
          );
        }

        return (
          selectedCount === requiredOptions &&
          this.selectedVariant &&
          this.quantity > 0 &&
          this.quantity <= this.availableStock
        );
      }

      // For non-variant products
      if (this.product.track_stock === 0 || this.product.track_stock === "0") {
        return this.quantity > 0;
      }

      // For pack/dynamic products, validate total units needed
      // Note: We validate quantity × unitAmount <= stock, NOT unitAmount <= stock
      if (this.isPackOrDynamic && this.unitAmount > 0) {
        const totalUnitsNeeded = this.quantity * this.unitAmount;
        return this.quantity > 0 && totalUnitsNeeded <= this.product.stock;
      }

      return this.quantity > 0 && this.quantity <= this.availableStock;
    },
    hasSelectedOptions() {
      return Object.keys(this.selectedVariationOptions).length > 0;
    },
    selectedOptionsCount() {
      return Object.keys(this.selectedVariationOptions).length;
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
    selectedVariationOptions: {
      handler() {
        // Reset quantity if it exceeds new available stock (only when track_stock is enabled)
        if (
          this.quantity > this.availableStock &&
          this.product.track_stock !== 0 &&
          this.product.track_stock !== "0"
        ) {
          this.quantity = this.availableStock;
        }
      },
      deep: true,
    },
    // Update unitAmount when product changes
    product: {
      immediate: true,
      handler(newProduct) {
        if (newProduct && this.isPackOrDynamic) {
          if (this.isDynamic) {
            const defaultAmount = newProduct?.size?.amount || 1;
            this.unitAmount = defaultAmount > 0 ? Number(defaultAmount) : 1;
          } else {
            const packAmount = newProduct?.size?.amount || 1;
            this.unitAmount = packAmount > 0 ? Number(packAmount) : 1;
          }
        }
      },
    },
    // Update quantity when unitAmount changes for pack/dynamic products
    unitAmount() {
      if (this.isPackOrDynamic && this.unitAmount > 0) {
        // Recalculate available stock and adjust quantity if needed
        if (
          this.quantity > this.availableStock &&
          this.product.track_stock !== 0 &&
          this.product.track_stock !== "0"
        ) {
          this.quantity = Math.max(1, this.availableStock);
        }
      }
    },
  },
  methods: {
    handleModalClose(value) {
      this.showModal = value;
      if (!value) {
        this.closeModal();
      }
    },
    handleOptionSelected(data) {
      this.quantity = 1;
      this.selectedVariationOptions = data.allSelectedOptions;
    },
    incrementQuantity() {
      if (this.quantity < this.availableStock) {
        this.quantity++;
      }
    },
    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    setQuantity(qty) {
      if (
        qty > 0 &&
        (this.product.track_stock === 0 ||
          this.product.track_stock === "0" ||
          qty <= this.availableStock)
      ) {
        this.quantity = qty;
      }
    },
    validateQuantity() {
      if (this.quantity < 1) {
        this.quantity = 1;
      } else if (
        this.quantity > this.availableStock &&
        this.product.track_stock !== 0 &&
        this.product.track_stock !== "0"
      ) {
        // If availableStock is 0, set quantity to 0 (can't buy any packs)
        // Otherwise, limit to availableStock
        this.quantity = this.availableStock > 0 ? this.availableStock : 0;
      }
    },
    resetForm() {
      this.quantity = 1;
      this.selectedVariationOptions = {};
      // Initialize unitAmount for pack/dynamic products
      if (this.isPackOrDynamic) {
        if (this.isDynamic) {
          // For dynamic products, start with size.amount if available, otherwise 1
          const defaultAmount = this.product?.size?.amount || 1;
          this.unitAmount = defaultAmount > 0 ? Number(defaultAmount) : 1;
        } else {
          // For pack products, use size.amount
          const packAmount = this.product?.size?.amount || 1;
          this.unitAmount = packAmount > 0 ? Number(packAmount) : 1;
        }
      } else {
        this.unitAmount = 1;
      }
    },
    clampUnitAmount() {
      const n = parseInt(this.unitAmount, 10);
      if (isNaN(n) || n < 1) {
        this.unitAmount = 1;
      } else {
        this.unitAmount = n;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    async addToCart() {
      if (!this.canAddToCart) return;

      try {
        this.loading = true;
        // Prepare cart data
        const cartData = {
          that: this,
          payload: this.product,
          selectedVariationOptions: this.product.has_variations
            ? this.selectedVariationOptions
            : null,
          qty: this.quantity, // Pass selected quantity
          // For pack/dynamic products, pass unitAmount and packQty
          packQty: this.isPackOrDynamic ? this.quantity : null,
          unitAmount: this.isPackOrDynamic ? this.unitAmount : null,
        };
        // Add to cart using store
        this.defineStore.addToCart(cartData);

        // Close modal
        this.closeModal();
      } catch (error) {
        console.error("Error adding to cart:", error);
        toast(this, "error_adding_to_cart", "error");
      } finally {
        this.loading = false;
      }
    },
    getStockColor(stock, trackStock) {
      // If track_stock is 0, show different color (don't track stock)
      if (trackStock === 0 || trackStock === "0") return "info";

      // For products that track stock
      if (stock <= 0) return "error";
      if (stock <= 5) return "warning";
      return "success";
    },
  },
};
</script>

<style scoped>
.product-selection-content {
  max-height: 85vh;
  overflow-y: auto;
}

/* Product Info Styles */
.product-image-container {
  border: 1px solid #e0e0e0;
}

/* Variations Section */
.variations-section {
  margin-bottom: 20px;
}

.variations-card {
  border-radius: 8px;
  overflow: hidden;
}

.variations-header {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 20px;
}

.variations-content {
  max-height: 400px;
  overflow-y: auto;
}

/* Quantity Styles */
.quantity-controls-container {
  text-align: center;
}

.quantity-display {
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
}

.quantity-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #1976d2;
  line-height: 1;
}

.quantity-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 4px;
}

.quantity-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.quantity-btn {
  border: 1px solid #e0e0e0;
}

.quantity-slider {
  flex: 1;
  max-width: 200px;
}

.quick-quantity {
  text-align: center;
}

/* Custom Action Buttons */
.custom-actions {
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  margin-top: 20px;
}

/* Sticky Sidebar */
.sticky-sidebar {
  position: sticky;
  top: 20px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .product-selection-content {
    max-height: 80vh;
  }

  .variations-content {
    max-height: 350px;
  }
}

@media (max-width: 960px) {
  .sticky-sidebar {
    position: static;
  }

  .product-selection-content {
    max-height: 75vh;
  }

  .variations-content {
    max-height: 300px;
  }
}

@media (max-width: 600px) {
  .product-selection-content {
    max-height: 70vh;
  }

  .variations-content {
    max-height: 250px;
  }

  .quantity-buttons {
    flex-direction: column;
    gap: 16px;
  }

  .quantity-slider {
    max-width: 100%;
  }

  .variations-header {
    padding: 12px 16px;
  }
}

/* Utility Classes */
.gap-2 {
  gap: 8px;
}

/* Focus States for Accessibility */
.v-btn:focus,
.v-slider:focus {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

/* Loading States */
.v-btn--loading {
  pointer-events: none;
}

/* Disabled States */
.v-btn--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Scrollbar Styling */
.variations-content::-webkit-scrollbar {
  width: 6px;
}

.variations-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.variations-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.variations-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
